import { useState } from "react";
import { motion } from "framer-motion";
import { CalendarPlus, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { useLang } from "@/i18n";
import { api, formatApiError } from "@/api";
import { SectionHead, Reveal } from "@/components/Widgets";

const CATEGORIES = ["Festa", "Sagra", "Mercato", "Musica", "Tradizione", "Degustazione", "Sport", "Altro"];

const inputCls = "w-full rounded-2xl border border-black/10 bg-white px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-turchese";

export default function ProposeEvent() {
  const { t } = useLang();
  const [f, setF] = useState({ name: "", email: "", title: "", description: "", start: "", end: "", place: "", category: "Festa", link: "", image: "" });
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/propose-event", f);
      setDone(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSending(false);
    }
  };

  if (done) {
    return (
      <div className="max-w-xl mx-auto px-4 py-24 text-center" data-testid="propose-thanks">
        <motion.div initial={{ scale: 0.7, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ type: "spring", stiffness: 260, damping: 18 }}>
          <span className="inline-flex w-20 h-20 rounded-full bg-pineta items-center justify-center mb-6">
            <CheckCircle2 className="w-9 h-9 text-white" />
          </span>
          <h1 className="font-heading font-bold text-3xl sm:text-4xl text-grafite">{t("propose.thanks")}</h1>
          <p className="mt-4 font-accent text-2xl text-mare">{t("propose.thanksSub")}</p>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 py-12 sm:py-16" data-testid="propose-page">
      <SectionHead kicker="Follonica Like" title={t("propose.title")} sub={t("propose.sub")} />
      <Reveal>
        <form onSubmit={submit} className="bg-sabbia/50 rounded-3xl p-6 sm:p-8 flex flex-col gap-4">
          <div className="grid sm:grid-cols-2 gap-4">
            <input required value={f.name} onChange={(e) => set("name", e.target.value)} placeholder={t("propose.name")} className={inputCls} data-testid="propose-name" />
            <input required type="email" value={f.email} onChange={(e) => set("email", e.target.value)} placeholder={t("propose.email")} className={inputCls} data-testid="propose-email" />
          </div>
          <input required value={f.title} onChange={(e) => set("title", e.target.value)} placeholder={t("propose.eventTitle")} className={inputCls} data-testid="propose-title" />
          <textarea rows={4} value={f.description} onChange={(e) => set("description", e.target.value)} placeholder={t("propose.desc")} className={inputCls} data-testid="propose-description" />
          <div className="grid sm:grid-cols-2 gap-4">
            <label className="block">
              <span className="block text-xs font-bold uppercase tracking-wider text-grafite/50 mb-1">{t("propose.start")}</span>
              <input required type="datetime-local" value={f.start} onChange={(e) => set("start", e.target.value)} className={inputCls} data-testid="propose-start" />
            </label>
            <label className="block">
              <span className="block text-xs font-bold uppercase tracking-wider text-grafite/50 mb-1">{t("propose.end")}</span>
              <input type="datetime-local" value={f.end} onChange={(e) => set("end", e.target.value)} className={inputCls} data-testid="propose-end" />
            </label>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <input value={f.place} onChange={(e) => set("place", e.target.value)} placeholder={t("propose.place")} className={inputCls} data-testid="propose-place" />
            <select value={f.category} onChange={(e) => set("category", e.target.value)} className={inputCls} data-testid="propose-category">
              {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <input value={f.link} onChange={(e) => set("link", e.target.value)} placeholder={t("propose.link")} className={inputCls} data-testid="propose-link" />
          <input value={f.image} onChange={(e) => set("image", e.target.value)} placeholder={t("propose.image")} className={inputCls} data-testid="propose-image" />
          <button type="submit" disabled={sending} data-testid="propose-submit"
            className="mt-2 rounded-full bg-terracotta hover:bg-[#d15f2d] disabled:opacity-60 text-white font-bold px-8 py-4 inline-flex items-center justify-center gap-2 transition-colors self-start">
            <CalendarPlus className="w-5 h-5" /> {sending ? t("survey.sending") : t("propose.submit")}
          </button>
        </form>
      </Reveal>
    </div>
  );
}
